import React from 'react';
import { Box, IconButton, styled } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { CustomerSegment } from 'components/customers/models/Customer';
import { Perk } from 'components/loyalty/models/PerkModel';
import { Row } from 'ui/Flex';
import { CampaignConditionData, CampaignEffectData, CampaignItem } from '../models/CampaignItem';
import { CampaignThenBlock } from './CampaignThenBlock';
import { CampaignWhenBlock } from './CampaignWhenBlock';
import clsx from 'clsx';

interface CreateCampaignItemProps {
    onSubmit: (item: CampaignItem, index: number) => void;
    onDelete: (index: number) => void;
    perks: Perk[];
    audiences: CustomerSegment[];
    item: CampaignItem;
    index: number;
    deletable: boolean;
}

const PREFIX = 'CreateCampaignItem';

const classes = {
    root: `${PREFIX}-root`,
    deleteIcon: `${PREFIX}-deleteIcon`
};

const StyledRow = styled(Row)(({ theme }) => ({
    '& .deleteIcon': {
        opacity: 0
    },
    '&:hover': {
        '& .deleteIcon': {
            opacity: 1
        }
    },
    [`& .${classes.deleteIcon}`]: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        transition: 'opacity 0.15s',
        marginRight: theme.spacing(1)
    }
}));

export const CreateCampaignItem: React.FC<CreateCampaignItemProps> = props => {
    const { perks, audiences, onSubmit, item, index, onDelete, deletable } = props;
    const { condition = null, effect = null, id = '' } = item || {};

    const handleConditionSubmit = React.useCallback(
        (data: CampaignConditionData) => {
            onSubmit({ id, effect, condition: data }, index);
        },
        [effect, id, onSubmit, index]
    );
    const handleEffectSubmit = React.useCallback(
        (data: CampaignEffectData) => {
            onSubmit({ id, condition, effect: data }, index);
        },
        [condition, id, onSubmit, index]
    );
    const handleDelete = React.useCallback(() => {
        onDelete(index);
    }, [onDelete, index]);
    return (
        <StyledRow valign="center" wrap="wrap" className={classes.root}>
            <Box marginRight={1}>
                <CampaignWhenBlock onSubmit={handleConditionSubmit} condition={condition} index={index} />
            </Box>
            {condition && <KeyboardArrowRightIcon color="primary" />}
            {condition && (
                <Box marginLeft={1}>
                    <CampaignThenBlock
                        index={index}
                        perks={perks}
                        audiences={audiences}
                        onSubmit={handleEffectSubmit}
                        effect={effect}
                    />
                </Box>
            )}
            {deletable && (
                <div className={clsx(classes.deleteIcon, 'deleteIcon')}>
                    {deletable && (
                        <IconButton color="primary" onClick={handleDelete} size="large">
                            <DeleteOutlineIcon />
                        </IconButton>
                    )}
                </div>
            )}
        </StyledRow>
    );
};
